import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * SyncStatus — Estado real do sistema (top-right no shell).
 *
 * Regra (briefing FASE 1): Nunca mostrar "Sincronizado" se Kommo, webhooks
 * ou fila estiverem com erro. Puxa /system/health-real (cache no backend)
 * a cada 60s e reflecte o pior estado detectado.
 *
 *  - verde: overall = ok
 *  - amarelo: overall = warn
 *  - vermelho: overall = error
 *  - cinza: unknown/loading
 *
 * Clicar → abre /estado-sistema.
 */
const COLORS = {
    ok: { bg: "var(--v2-success, #10b981)", label: "Saudável", title: "Todos os serviços OK" },
    warn: { bg: "#f59e0b", label: "Avisos", title: "Alguns serviços com aviso" },
    error: { bg: "var(--v2-danger, #ef4444)", label: "Erro", title: "Falhas críticas" },
    unknown: { bg: "#9ca3af", label: "A verificar…", title: "A carregar estado" },
};

export default function SyncStatus() {
    const [state, setState] = useState({ overall: "unknown", lastAt: null });
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get("/system/health-real");
                if (!cancelled) setState({ overall: r.data?.overall || "unknown", lastAt: Date.now() });
            } catch {
                // fallback ao /kommo/status para não desaparecer se health-real falhar
                try {
                    const r2 = await api.get("/kommo/status");
                    if (!cancelled) setState({ overall: r2.data?.connected ? "ok" : "error", lastAt: Date.now() });
                } catch {
                    if (!cancelled) setState({ overall: "error", lastAt: Date.now() });
                }
            }
        };
        load();
        const iv = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    useEffect(() => {
        const iv = setInterval(() => setTick((t) => t + 1), 15000);
        return () => clearInterval(iv);
    }, []);

    const secsAgo = state.lastAt ? Math.floor((Date.now() - state.lastAt) / 1000) : null;
    const collapse = secsAgo !== null && secsAgo > 5;
    const meta = COLORS[state.overall] || COLORS.unknown;
    const label = state.overall === "ok"
        ? (secsAgo !== null && secsAgo < 60 ? "Sincronizado" : `Há ${Math.floor(secsAgo / 60)}min`)
        : meta.label;

    return (
        <Link
            to="/estado-sistema"
            className="hidden sm:flex items-center gap-1.5 h-7 px-2 rounded-md border text-[11px] font-body hover:border-zinc-900 transition-colors"
            style={{ borderColor: "var(--v2-border)", color: "var(--v2-muted)" }}
            title={meta.title + " — clica para ver detalhe"}
            data-testid="v2-sync-status"
        >
            <span className="relative flex h-2 w-2">
                {state.overall === "ok" && (
                    <span
                        className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                        style={{ background: meta.bg }}
                    />
                )}
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: meta.bg }} />
            </span>
            {!collapse && <span className="tabular-nums">{label}</span>}
        </Link>
    );
}
